import {
  Badge,
  Button,
  Card,
  Empty,
  Flex,
  Heading,
  ITreeProps,
  Input,
  List,
  Select,
  Spacer,
  Spinner,
  Text,
  Tooltip,
  Tree,
} from '@oliasoft-open-source/react-ui-library';
import { getTokenPayload } from '@oliasoft/pkce';
import { ActionMap, ability } from '@oliasoft/authorization-ui';
import { caslEnums } from 'common/enums/casl-enums';
import { downloadObject } from 'client/components/data-viewer/utils/export';
import { FileUpload } from 'client/components/file-upload/file-upload';
import { ShareObjectModal } from 'client/components/object/share-object-modal';
import translations from 'client/internationalization/translation-map.json';
import { getUsers } from 'client/store/entities/user/user';
import { GoDotFill } from 'react-icons/go';
import {
  createNode, getDesignNodes,
  getNodes, nodesReset, updateNode,
} from 'client/store/entities/hierarchy/node/node';
import {
  addStructuredData,
  getObjectsOfNode,
  moveObjectToNode,
  objectsReset,
  preUnpackObject,
  unpackObject,
} from 'client/store/entities/object/object';
import {
  HierarchyLevelType, INode, IObject,
} from 'common/interfaces/hierarchy-entities';
import {
  Dispatch,
  ReactElement,
  SetStateAction, useEffect, useMemo,
  useState, useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { TbFolder, TbFolderOpen } from 'react-icons/tb';
import {
  ConnectedProps, connect, useDispatch, useSelector,
} from 'react-redux';
import ClientConfig from 'client/config';
import { AccessSubjectTypes } from 'common/interfaces/access-rights';
import { ClassificationLevelControlModal } from 'client/components/classification-level-control-modal/classification-level-control-modal';
import { RootState } from 'client/store/configureStore';
import { getEntityAccessRightsList } from 'client/store/entities/access-rights/access-rights';
import { setActiveItem } from 'client/store/ui/hierarchy-tree/hierarchy-tree';
import { PossibleEDMDTables, RecognisedDataCatalogues, RecognisedDataObjects, RecongnizebleFileFormats } from 'common/enums/known-data-enums';
import { IUser } from 'common/interfaces/customer-interfaces';
import { getUnitTemplateLoadingSelector, getUnitTemplateQuantitiesGroupedSelector } from 'client/store/entities/unit-templates/selectors';
import { NodeCreateParentPayload } from 'client/store/entities/hierarchy/helpers/hierarchy-api-actions-factory';
import { isMultipleTableCatalogue, isMultipleTableType } from 'common/utils/is-multiple-tables';
import { EntityType, WellStatusType } from 'common/enums/entity-enums';
import { AccessControlModal, IAccessControlModalsRef } from '@oliasoft/hierarchy-gui';
import { useSearchParams } from 'react-router-dom';
import { getIsLinked } from 'common/utils/is-linkable-table';
import { setAccessRightsBatch } from 'client/components/access-control-modal/access-utils';
import {
  dataObjectToTreeItem, getChildHierarchyEntityType, hierarchyLevelTypeNamePlural, hierarchyTypeAddLabel, nodeToTreeItem,
} from 'client/views/explore/hierarchy/hierarchy.utils';
import { UnpackModal } from '../../../components/edm/unpack-modal';
import { getOneEntityAction, mapTreeItemToEntity } from '../hierarchy/hierarchy-entities-factory';
import { HierarchyItemStates } from '../hierarchy/hierarchy-enums';
import { HierarchyTreeItems, IHierarchyTreeItem } from '../hierarchy/hierarchy.interfaces';
import { DataExplorerFileDrop } from './data-explorer-file-drop';
import { getObjectName, getRestrictedName } from '../hierarchy/object-parts';
import { resolveAssociatedDataName } from 'common/services/configs/trajectories/associated-data/associated-data-name-resolver';
import { getDataIsEmpty } from 'common/utils/get-data-is-empty';
import { getSurveyRunLinkedObjectIds } from 'client/store/entities/object/selectors';
import { FilesBrowser } from './files-browser';
import type { IFilesBrowserItem } from './files-browser.utils';
import { ROOT_FOLDER_ID } from './move-targets';
import { useMoveFile } from './use-move-file';

const recognisedDataSortingMap: { [key: string]: number } = {
  [RecognisedDataCatalogues.ProjectData]: 1.1,
  [RecognisedDataCatalogues.CoordinateReferenceSystem]: 2.1,
  [RecognisedDataCatalogues.SiteReferenceSystem]: 2.2,
  [RecognisedDataCatalogues.WellboreWellheadPosition]: 2.3,
  [RecognisedDataCatalogues.PorePressure]: 3.1,
  [RecognisedDataCatalogues.FracturePressure]: 3.2,
  [RecognisedDataCatalogues.CollapsePressure]: 3.3,
  [RecognisedDataCatalogues.MHSPressure]: 3.4,
  [RecognisedDataCatalogues.GeothermalTemperature]: 3.5,
  [RecognisedDataCatalogues.TrajectoryPlan]: 4.1,
  [RecognisedDataCatalogues.SurveyRuns]: 4.2,
  [RecognisedDataCatalogues.SurveyProgram]: 4.3,
  [RecognisedDataCatalogues.DefinitiveSurveyList]: 4.4,
  [RecognisedDataCatalogues.WellborePositionUncertainty]: 4.5,
  [RecognisedDataCatalogues.UncertaintyVectors]: 4.6,
  [RecognisedDataCatalogues.GeomagneticParametersDefinition]: 4.7,
  [RecognisedDataCatalogues.CovarianceMatrixTotal]: 4.8,
  [RecognisedDataCatalogues.HoleSize]: 4.9,
};

const covarianceSubNodeNumbers = {
  [RecognisedDataObjects.CovExternal]: '4.8.1',
  [RecognisedDataObjects.CovWelldesign]: '4.8.2',
};

enum SortByType {
  Name = 'Name',
  Number = 'Number',
  Completed = 'Completed',
}


type DataExplorerProps = PropsFromRedux & {
  hierarchy: HierarchyTreeItems,
  selectedArchiveId: string | null;
  selectedData: IObject | null;
  setSelectedData: Dispatch<SetStateAction<IObject | null>>;
  setDataViewerVisible: (bool: boolean) => void;
  createItem: (entityType: HierarchyLevelType, parentItem: IHierarchyTreeItem | null) => void;
  editItem: (item: IHierarchyTreeItem, type: HierarchyItemStates) => void;
  deleteItem: (item: IHierarchyTreeItem, type: HierarchyItemStates) => void;
  loadMore: (parentItem: IHierarchyTreeItem) => void;
  getIsLoading: (item: IHierarchyTreeItem | null) => boolean;
  getTotalChildCount: (loadType: HierarchyLevelType, parentId: string) => number | undefined;
  openTreeNodes: string[]
};

const DataExplorer = ({
  selectedData,
  setSelectedData,
  setDataViewerVisible,
  getObjectsOfNode,
  hierarchy,
  setActiveItem,
  activeItem,
  objects,
  objectsLoading,
  getNodes,
  getDesignNodes,
  createNode,
  updateNode,
  nodes,
  nodesLoading,
  isLoadingIPMFiles,
  nodesPages,
  createItem,
  editItem,
  deleteItem,
  preUnpackObject,
  unpackObject,
  loadMore,
  getIsLoading,
  getTotalChildCount,
  users,
  getUsers,
  selectedCustomer,
  selectedArchiveId,
  addStructuredData,
  moveObjectToNode,
  entityAccessRightsList,
  entityAccessRightsMetadata,
  openTreeNodes
}: DataExplorerProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const childRef = useRef<IAccessControlModalsRef>(null);
  const companyName = selectedCustomer?.companyName;

  const [searchParams] = useSearchParams();
  const designid = searchParams.get('activeEntityId');
  const surveyRunLinkedObjectIds = useSelector((state: RootState) => getSurveyRunLinkedObjectIds(state, designid));

  const tokenPayload = getTokenPayload(
    ClientConfig.config.pkce.provider,
    ClientConfig.config.applicationId,
    ClientConfig.config.idpServerUrl,
  );

  const [isOpenUploadModal, setOpenUploadModal] = useState<boolean>(false);
  const [isOpenUnpackModal, setOpenUnpackModal] = useState<boolean>(false);
  const [isOpenShareModal, setOpenShareModal] = useState<boolean>(false);
  const [nodeId, setNodeId] = useState<string>();
  const [loadedNodes, setLoadedNodes] = useState<{ [key: string]: true; }>({});
  const [files, setFiles] = useState<File[] | undefined>();
  const [isOpenClassificationLevelControlModal, setOpenClassificationLevelControlModal] = useState(false);
  const [classificationLevelHierarchyItem, setClassificationLevelHierarchyItem] = useState<IHierarchyTreeItem | undefined>();
  const isLoadingUnitTemplates = useSelector(getUnitTemplateLoadingSelector);
  const unitTemplateQuantitiesGrouped = useSelector(getUnitTemplateQuantitiesGroupedSelector);
  const [filterText, setFilterText] = useState('');
  const [sortByvalue, setSortByValue] = useState(SortByType.Number);

  const closeUnpackModal = () => setOpenUnpackModal(false);
  const closeShareModal = () => setOpenShareModal(false);
  const openAccessControlModal = (item: IHierarchyTreeItem, subjectType = AccessSubjectTypes.User) => {
    childRef.current?.accessRightsConfigurationStarted({
      item,
      userList: users,
      currentUserSubjectId: tokenPayload!.subjectId || '',
      subjectType,
    });
    dispatch(getEntityAccessRightsList(item.entityType, item.id, subjectType));
  };

  const onCloseClassificationLevelControlModal = () => setOpenClassificationLevelControlModal(false);
  const onOpenClassificationLevelControlModal = (item: IHierarchyTreeItem) => {
    setClassificationLevelHierarchyItem(item);
    setOpenClassificationLevelControlModal(true);
  };

  const hierarchyActiveItem: IHierarchyTreeItem | null = useMemo(
    () => {
      const item = activeItem ? hierarchy[activeItem.id] : null;
      return item
        ? {
          ...item,
          key: item.id,
          parent: item.parentId || 0,
        }
        : null;
    },
    [activeItem, hierarchy],
  );

  const canMangeAccess = ability.can(ActionMap.MANAGE, caslEnums.manageAccessRights);
  const activeItemIsDesign = activeItem?.entityType === HierarchyLevelType.Design;

  const viewData = (data: IObject) => {
    setSelectedData(data);
    setDataViewerVisible(true);
  };

  const resetNodesObjects = () => {
    setLoadedNodes({});
    dispatch(objectsReset());
    dispatch(nodesReset());
    if (!activeItem) return;

    if (hierarchyActiveItem?.nodeId) {
      getNodes({ parentId: hierarchyActiveItem?.nodeId, page: 1, parenttype: HierarchyLevelType.Node });
    } else if (activeItemIsDesign) {
      getDesignNodes(activeItem.id);
    }
  };

  useEffect(() => {
    childRef.current?.accessRightListUpdated({ accessRightsList: entityAccessRightsList, metadata: entityAccessRightsMetadata });
  }, [entityAccessRightsList]);

  useEffect(() => {
    resetNodesObjects();
    setSortByValue(SortByType.Number);
  }, [activeItem, selectedCustomer]);

  useEffect(() => {
    if (companyName) {
      getUsers();
    }
  }, [selectedCustomer]);

  useEffect(() => {
    if (!Object.keys(unitTemplateQuantitiesGrouped)?.length) {
      return;
    }

    const nodesIds = nodes.map((node: INode) => node.nodeid);
    const loadedNow: any = {};
    nodesIds.forEach((nodeId: string) => {
      loadedNow[nodeId] = true;
      if (loadedNodes[nodeId]) return;
      const page = nodesPages[nodeId] || 1;
      const params = { parentId: nodeId, page };
      getNodes({ ...params, parenttype: HierarchyLevelType.Node });
      if (nodeId && Object.keys(unitTemplateQuantitiesGrouped)?.length) {
        getObjectsOfNode(nodeId, unitTemplateQuantitiesGrouped);
      }
    });
    setLoadedNodes({ ...loadedNow });
  }, [nodes, isLoadingUnitTemplates, unitTemplateQuantitiesGrouped]);

  const isLoading = useMemo(() => Object.values(nodesLoading).includes(true) || Object.values(objectsLoading).includes(true) || isLoadingUnitTemplates || isLoadingIPMFiles, [nodesLoading, objectsLoading, isLoadingUnitTemplates, isLoadingIPMFiles]);

  // A folder's status always includes its sub-folders
  const getNodeObjects = (nodeId: string): IObject[] => {
    const childNodes = nodes.filter((node: INode) => node.parentid === nodeId);
    const childObjects = childNodes.flatMap((child: INode) => getNodeObjects(child.nodeid));
    return [...(objects[nodeId] || []), ...childObjects];
  };

  const nodeDataStatus = (nodeId: string) => {
    const nodeObjects = getNodeObjects(nodeId);
    if (!nodeObjects.length) return -1;
    const populated = nodeObjects.filter((object: IObject) => {
      return !getDataIsEmpty(object.data, getIsLinked(object).isLinked);
    });
    return populated.length / nodeObjects.length;
  };

  const getNodeObjectIndicator = (nodeId: string) => {
    const status = nodeDataStatus(nodeId);
    if (status === 1) return <GoDotFill style={{ color: 'var(--color-text-success)' }} />;
    if (status > 0) return <GoDotFill style={{ color: 'var(--color-text-warning)' }} />;
    return <GoDotFill style={{ color: 'var(--color-text-error)' }} />;
  };

  const addHierarchyNode = (onComplete: Function) => {
    if (activeItem) {
      const entity = {
        name: `${hierarchyActiveItem!.name} ${t(translations.catalogue)}`,
        parent: { type: activeItem!.entityType, id: activeItem!.id },
        entityType: HierarchyLevelType.Node,
      };
      const newNode = mapTreeItemToEntity(entity);
      createNode({ ...newNode, parent: entity.parent as unknown as NodeCreateParentPayload['parent'] }, (id: string) => {
        dispatch(getOneEntityAction(hierarchyActiveItem!));
        resetNodesObjects();
        onComplete(id);
      });
    }
  };
  const addNode = (nodeId?: string | undefined) => {
    if (nodeId) {
      const nodeParent: IHierarchyTreeItem = {
        id: nodeId,
        key: nodeId,
        name: '',
        entityType: HierarchyLevelType.Node,
        parent: 0,
      };
      createItem(HierarchyLevelType.Node, nodeParent);
    } else if (hierarchyActiveItem!.nodeId) {
      createItem(HierarchyLevelType.Node, hierarchyActiveItem);
    } else if (activeItemIsDesign) {
      const nodeParent: IHierarchyTreeItem = {
        id: activeItem.id,
        key: activeItem.id,
        name: '',
        entityType: HierarchyLevelType.Design,
        parent: 0,
      };
      createItem(HierarchyLevelType.Node, nodeParent);
    } else {
      addHierarchyNode((newId: string) => {
        const parent = {
          id: newId,
          key: newId,
          name: hierarchyActiveItem!.name || '',
          entityType: HierarchyLevelType.Node,
          parent: activeItem?.id || -1,
        };

        createItem(HierarchyLevelType.Node, parent);
      });
    }
  };

  const deleteNode = (item: IHierarchyTreeItem) => {
    deleteItem(item, HierarchyItemStates.Delete);
  };

  const openUploadModal = (nodeId: string | undefined) => {
    if (nodeId) {
      setNodeId(nodeId);
      setOpenUploadModal(true);
    } else {
      addHierarchyNode((newId: string) => {
        setNodeId(newId);
        setOpenUploadModal(true);
      });
    }
  };

  const closeUploadModal = () => {
    setOpenUploadModal(false);
    setNodeId(undefined);
    setFiles(undefined);
  };

  const onUploadComplete = (nodeId: string | undefined) => {
    if (nodeId) {
      getObjectsOfNode(nodeId, unitTemplateQuantitiesGrouped);
    }
    setNodeId(undefined);
    setFiles(undefined);
  };

  const onDrop = (files: File[], nodeId?: string) => {
    if (!files.length) return;
    setFiles(files);
    openUploadModal(nodeId || hierarchyActiveItem?.nodeId);
  };

  const objectArray = Object.values(objects).reduce((allObjects, nodeObjects) => allObjects.concat(nodeObjects), []);
  const { requestMove: requestMoveFile, renderDialog: renderMoveFileDialog } = useMoveFile({
    nodes,
    objects: objectArray,
    rootParentNode: hierarchyActiveItem?.nodeId
      ? { nodeid: hierarchyActiveItem.nodeId, name: hierarchyActiveItem.name }
      : undefined,
    moveObjectToNode,
  });

  const getRootParentPayload = () => {
    if (activeItemIsDesign) {
      return { parent: { type: activeItem!.entityType, id: activeItem!.id } };
    }
    return { parentid: hierarchyActiveItem!.nodeId };
  };

  const moveNode = (nodeId: string, targetFolderId: string | number) => {
    const originalNode = nodes.find((node: INode) => node.nodeid === nodeId);
    if (!originalNode) return;
    const {
      nodeid, name, role, classificationlevel, locked,
    } = originalNode;
    const parentPayload = targetFolderId === ROOT_FOLDER_ID ? getRootParentPayload() : { parentid: String(targetFolderId) };
    updateNode({
      nodeid, name, role, classificationlevel, ...parentPayload, ...(locked !== undefined && { locked }),
    });
  };

  const handleMoveItem = (item: IFilesBrowserItem, targetFolderId: string | number) => {
    if (item.entityType === HierarchyLevelType.Node) {
      return moveNode(String(item.id), targetFolderId);
    }
    requestMoveFile(String(item.id), targetFolderId);
  };

  const itemHasChild: ITreeProps['itemHasChild'] = ({ data }) => {
    return data?.entityType === HierarchyLevelType.Node;
  };

  const getAccessManagementAction = (item: IHierarchyTreeItem, disabled = false) => {
    return (canMangeAccess) ? {
      type: 'Option',
      label: t(translations.authorization_accessControl),
      icon: 'key',
      disabled,
      onClick: () => openAccessControlModal(item),
    } : null;
  };

  const getClassificationLevelManagementAction = (item: IHierarchyTreeItem, disabled = false) => {
    const canMangeClassificationLevel = ability.can(ActionMap.MANAGE, caslEnums.nodeSecurityClassification);
    return (canMangeClassificationLevel) ? {
      type: 'Option',
      label: t(translations.classificationLevel),
      icon: 'key',
      disabled,
      onClick: () => {
        onOpenClassificationLevelControlModal(item);
      },
    } : null;
  };

  const hierarchyChildren = Object.values(hierarchy).filter((item) => item.parent === activeItem?.id && item.entityType !== HierarchyLevelType.Node);

  const getChildHierarchyItems = () => {
    const items = hierarchyChildren.map((item) => {
      const accessManagementAction = getAccessManagementAction(item);

      return ({
        id: item.id,
        name:
          <>
            <Text>{item.name}</Text>
            {item.entityType === HierarchyLevelType.Design && item?.well_status && <Text faint><span style={{ color: item.well_status === WellStatusType.Actual ? 'var(--color-info-300)' : '' }}>{` ${item.well_status}`}</span></Text>}
          </>,
        onClick: () => {
          const { onClick, ...itemClean } = item;
          setActiveItem(itemClean);
        },
        actions: [{
          label: 'Menu',
          subActions: [
            {
              type: 'Option', icon: 'edit', label: t(translations.edit), onClick: () => editItem(item, HierarchyItemStates.Edit),
            },
            {
              type: 'Option', icon: 'delete', label: t(translations.delete), onClick: () => deleteItem(item, HierarchyItemStates.Delete),
            },
            ...(canMangeAccess ? [accessManagementAction] : []),
          ],
        }],
      });
    }) as { id: string, name: string | ReactElement, onClick?: () => void, actions?: any }[];

    const totalLength = getTotalChildCount(getChildHierarchyEntityType(activeItem!.entityType)!, activeItem!.id);
    if (!totalLength || items.length < totalLength) {
      items.push(getIsLoading(hierarchyActiveItem || null) ? {
        id: 'loading-more',
        name: <Text small muted>{t(translations.hierarchy_loading)}</Text>,
      } : {
        id: 'load-more',
        name: <Text small>{t(translations.hierarchy_loadMore)}</Text>,
        onClick: () => loadMore(hierarchyActiveItem!),
      });
    }
    return items;
  };

  const getChildCount = () => {
    const currentCount = hierarchyChildren.length;
    const totalCount = getTotalChildCount(getChildHierarchyEntityType(activeItem!.entityType)!, activeItem!.id);
    if (currentCount === 0) return '';
    return totalCount && totalCount > currentCount ? `(${currentCount}/${totalCount})` : `(${currentCount})`;
  };

  const getHierarchyItemHeader = () => {
    return activeItem ? hierarchyLevelTypeNamePlural[getChildHierarchyEntityType(activeItem.entityType)!]?.concat(` ${getChildCount()}`)
      : '';
  };

  const hierarchyItemChildList = activeItem && (
    <div>
      <Flex justifyContent="space-between" alignItems="center" wrap={false}>
        <Heading>{getHierarchyItemHeader()}</Heading>
        <Button
          label={hierarchyTypeAddLabel[getChildHierarchyEntityType(activeItem.entityType)!]}
          onClick={() => createItem(getChildHierarchyEntityType(activeItem.entityType)!, hierarchyActiveItem)}
          small
        />
      </Flex>
      <Spacer />
      {hierarchyChildren.length > 0 && (
        <Card padding={false}>
          <List
            noHeader
            list={{
              items: getChildHierarchyItems(),
            }}
          />
        </Card>
      )}
    </div>
  );

  const usersById = new Map((users || []).map((user: IUser) => [user.subjectId, user]));
  const nodesById = new Map(nodes.map((node: INode) => [node.nodeid, node]));

  const objectListItems = objectArray.map((object) => {
    const owner = usersById.get(object.owner);
    const { objectid } = object;
    const objectRawName = resolveAssociatedDataName(object, object.associateddata);
    const treeItem = ({
      id: objectid,
      key: objectid,
      rawName: objectRawName || object.name,
      name: getObjectName(object, object.associateddata, companyName!, owner!),
      active: selectedData === object,
      parent: object.nodeid === hierarchyActiveItem?.nodeId ? 0 : object.nodeid,
      // not updated on move
      isRecognisedData: !!recognisedDataSortingMap[nodesById.get(object.nodeid)?.name as string],
      onClick: () => (!object.restricted ? viewData(object) : null),
      entityType: HierarchyLevelType.Object,
      objectType: object.type,
      depthMD: object.associateddata?.depthMD || object?.data?.[1]?.depthMD, // common sorter for majority of fields that depend on depth
      actions: [] as any[],
      restricted: object.restricted,
      onDrop: undefined as any,
    });
    const donwloadAction = {
      type: 'Option', icon: 'download', label: t(translations.importExport_download), onClick: () => { downloadObject(object); },
    };
    // OW-26614
    // const shareActions = {
    //   type: 'Option',
    //   icon: <TbFlagShare />,
    //   label: t(translations.share),
    //   onClick: () => {
    //     setSelectedData(object);
    //     setOpenShareModal(true);
    //   },
    // };
    const isEdm = object.type === RecongnizebleFileFormats.Edm;
    const unpackAction = isEdm ? {
      type: 'Option',
      icon: 'expand',
      label: t(translations.edm_unpack),
      onClick: () => {
        setSelectedData(object);
        setOpenUnpackModal(true);
      },
    } : null;
    const isStructuredObject = object.type ? object.type in PossibleEDMDTables : false;
    const isMultiple = isMultipleTableType(object.type as EntityType);
    const isCovWelldesignObject = nodesById.get(object.nodeid)?.name === RecognisedDataObjects.CovWelldesign;
    const isObjectUsed = !!surveyRunLinkedObjectIds[object.objectid]
    const deleteTooltipMessage = isObjectUsed ? 'Can not delete item since its used in Survey Run' : '';
    const deleteActionOption = {
      type: 'Option',
      icon: 'delete',
      label: <Tooltip text={deleteTooltipMessage}><Text>{t(translations.delete)}</Text></Tooltip>,
      onClick: () => deleteItem({ ...object, ...treeItem }, HierarchyItemStates.Delete),
      disabled: isObjectUsed,
    }
    const deleteAction = isStructuredObject && !isMultiple
      ? [{
        type: 'Option', icon: 'delete', label: 'Clear data', onClick: () => deleteItem({ ...object, ...treeItem }, HierarchyItemStates.Delete),
      }]
      : [{
        type: 'Option', icon: 'edit', label: t(translations.edit), onClick: () => editItem({ ...object, ...treeItem, name: object.name }, HierarchyItemStates.Edit),
      },
        deleteActionOption,
      ];
    const accessManagementAction = getAccessManagementAction(dataObjectToTreeItem(object));
    const classificationLevelManagementAction = getClassificationLevelManagementAction(dataObjectToTreeItem(object));

    treeItem.actions = [{
      subActions: [
        donwloadAction,
        // shareActions, OW-26614
        ...(unpackAction ? [unpackAction] : []),
        ...(isCovWelldesignObject ? [] : deleteAction),
        ...(canMangeAccess ? [accessManagementAction] : []),
        ...(classificationLevelManagementAction ? [classificationLevelManagementAction] : []),
      ],
    }];

    if (treeItem.restricted) {
      treeItem.actions[0].subActions = treeItem.actions[0].subActions.map((subAction: any[]) => ({ ...subAction, disabled: true }));
    }

    return treeItem;
  });

  const getNodeChildrenCount = (nodeId: string): number => {
    const nodeItems: INode[] = nodes;
    const arr = [...nodeItems, ...objectListItems];
    return arr.reduce((count: number, item: any) => {
      if (item.parent === nodeId) {
        return count + 1;
      }
      if (item.parentid === nodeId && item.nodeid) {
        return count + getNodeChildrenCount(item.nodeid) || 0;
      }
      return count;
    }, 0);
  };

  const compareNodes = (a: INode, b: INode): number => {
    if (!recognisedDataSortingMap[a.name] && !recognisedDataSortingMap[b.name]) return 0;
    if (!recognisedDataSortingMap[a.name]) return 1;
    if (!recognisedDataSortingMap[b.name]) return -1;
    return recognisedDataSortingMap[a.name] - recognisedDataSortingMap[b.name];
  };

  const getNodeName = (node: INode) => {
    if (node.restricted) {
      const owner = users?.find((user: IUser) => user.subjectId === node.owner);
      return getRestrictedName(node, companyName!, owner!);
    }

    const isCovarianceSubNode = nodesById.get(node.parentid!)?.name === RecognisedDataCatalogues.CovarianceMatrixTotal;
    //only COV External can be renamed
    const covarianceSubNodeNumber = node.name === RecognisedDataObjects.CovWelldesign
      ? covarianceSubNodeNumbers[RecognisedDataObjects.CovWelldesign]
      : covarianceSubNodeNumbers[RecognisedDataObjects.CovExternal];
    const nodeNumberPrefix = isCovarianceSubNode ? covarianceSubNodeNumber : recognisedDataSortingMap[node.name];

    return (
      <>
        {nodeNumberPrefix}
        {' '}
        {node.name}
      </>
    );
  };

  const nodeListItems = useMemo(() => {
    const nodeItems = [...nodes];
    if (activeItemIsDesign) {
      nodeItems.sort(compareNodes);
    }

    return nodeItems.map((item) => {
      const { restricted } = item;
      const isRecognisedData = recognisedDataSortingMap[item.name];
      const isCovWelldesignNode = item.name === RecognisedDataObjects.CovWelldesign;
      const isRootNode = item.parentid === hierarchyActiveItem?.nodeId;
      const isDesignNode = (activeItemIsDesign && !item.parent && !item.parentid);
      const parent = (isRootNode || isDesignNode) ? 0 : (item.parentid || -1);

      const accessManagementAction = getAccessManagementAction(nodeToTreeItem(item), restricted);
      const classificationLevelManagementAction = getClassificationLevelManagementAction(nodeToTreeItem(item), restricted);
      const isMultiple = isRecognisedData && !nodes.some((n: INode) => n.parentid === item.nodeid) && isMultipleTableCatalogue(item?.name as RecognisedDataCatalogues);
      // TODO: better way to get designid Volodymyr
      const recognisedDataActions = isMultiple
        ? [
          {
            type: 'Option',
            icon: 'add',
            label: `${t(translations.add)} table`,
            onClick: () => addStructuredData(item, designid!),
            disabled: restricted,
          },
        ]
        : [];

      const treeItem = ({
        id: item.nodeid,
        key: item.nodeid,
        rawName: item.name,
        name: getNodeName(item),
        active: false,
        parent,
        isRecognisedData: !!isRecognisedData,
        entityType: HierarchyLevelType.Node,
        objectType: undefined as string | undefined,
        depthMD: undefined as number | string | undefined,
        onClick: null as any,
        actions: [{
          icon: getNodeObjectIndicator(item.nodeid),
        },
        {
          icon: <Badge color="var(--color-border)" title={getNodeChildrenCount(item.nodeid)} />,
        }] as any[],
        onDrop: undefined as any,
      });
      treeItem.actions = [
        ...treeItem.actions,
        {
          subActions: [
            ...(ability.can(ActionMap.MANAGE, caslEnums.structuredDataEntities) ? [{
              type: 'Option',
              icon: 'upload',
              label: t(translations.importExport_fileUpload),
              onClick: () => openUploadModal(item.nodeid),
              disabled: restricted,
            }] : []),
            ...(isRecognisedData || isCovWelldesignNode)
              ? recognisedDataActions
              : [
                {
                  type: 'Option', icon: 'add', label: `${t(translations.add)} ${t(translations.catalogue)}`, onClick: () => addNode(item.nodeid), disabled: restricted,
                },
                {
                  type: 'Option', icon: 'edit', label: t(translations.edit), onClick: () => editItem({ ...item, ...treeItem, name: item.name }, HierarchyItemStates.Edit), disabled: restricted,
                },
                {
                  type: 'Option', icon: 'delete', label: t(translations.delete), onClick: () => deleteNode({ ...item, ...treeItem, name: item.name }), disabled: restricted,
                }],
            ...(accessManagementAction ? [accessManagementAction] : []),
            ...(classificationLevelManagementAction ? [classificationLevelManagementAction] : []),
          ],
        }];
      if (!isRecognisedData) {
        treeItem.onDrop = (event: React.DragEvent<HTMLDivElement>) => {
          onDrop(Array.from(event.dataTransfer.files || []), item.nodeid);
        };
      }
      return treeItem;
    });
  }, [nodes, objects]);

  const empty = (structuredData: boolean) => (
    <div>
      <Flex justifyContent="center" alignItems="center">
        <Empty text={activeItemIsDesign && structuredData ? (
          <>
            <Text>{t(translations.importExport_noUnstructuredData)}</Text>
            <Spacer height="var(--padding-xs)" />
            <Button
              colored
              small
              label={`${t(translations.add)} ${t(translations.catalogue)}`}
              onClick={() => addNode(hierarchyActiveItem!.nodeId)}
            />
          </>
        ) : (
          <>
            <Text>{t(translations.importExport_noFilesUploadedYet)}</Text>
            {' '}
            <Button
              colored
              small
              label={t(translations.importExport_uploadFile)}
              onClick={() => openUploadModal(hierarchyActiveItem!.nodeId)}
            />
          </>
        )}
        />
      </Flex>
    </div>
  );

  const emptySearch = (
    <div>
      <Flex justifyContent="center" alignItems="center">
        <Empty text="No data matching search" />
      </Flex>
    </div>
  );

  const sortByOptions = [
    {
      label: `${t(translations.sortBy)} ${t(translations.number)}`,
      value: SortByType.Number,
    },
    {
      label: `${t(translations.sortBy)} ${t(translations.name)}`,
      value: SortByType.Name,
    },
    {
      label: `${t(translations.sortBy)} ${t(translations.completed)}`,
      value: SortByType.Completed,
    },
  ];

  const renderDataList = (structuredData = false) => {
    const covarianceMatrixNode = nodeListItems.find(n => n.rawName === RecognisedDataCatalogues.CovarianceMatrixTotal);

    const separatedNodeList = activeItemIsDesign
      ? nodeListItems.filter(item => {
        const isRecognised = Object.values(RecognisedDataCatalogues as { [key: string]: string }).includes(item.rawName);
        const isCovarianceSubNode = covarianceMatrixNode ? item.parent === covarianceMatrixNode.id : false;
        const belongsToStructuredData = isRecognised || isCovarianceSubNode;
        return structuredData ? belongsToStructuredData : !belongsToStructuredData;
      })
      : nodeListItems;

    const separatedObjectList = activeItemIsDesign ? objectListItems.filter(object => separatedNodeList.filter((item) => object.parent === item.id).length > 0) : objectListItems;

    const unfilteredDataListItems = separatedNodeList.concat(separatedObjectList);
    const dataListItems = filterText === '' || !structuredData ? unfilteredDataListItems : unfilteredDataListItems.map(item => {
      if (item.parent !== 0 && item.rawName.toLowerCase().includes(filterText.toLowerCase())) {
        return { ...item, parent: 0 };
      }
      return { ...item, parent: -1 };
    });
    const sortedList = structuredData ? dataListItems.sort((a, b) => {
      // Always sort objects, survey runs by depthMD also covariance matrixes cause it looks nicer, others alphabetically
      if (a.entityType === HierarchyLevelType.Object && b.entityType === HierarchyLevelType.Object) {

        const isSurveyRun = a.objectType === EntityType.SurveyRun && b.objectType === EntityType.SurveyRun;
        const isCovarianceMatrix = a.objectType === EntityType.CovarianceMatrix && b.objectType === EntityType.CovarianceMatrix;
        if (isSurveyRun || isCovarianceMatrix) {
          return Number(a.depthMD ?? 0) - Number(b.depthMD ?? 0);
        }
        return a.rawName.localeCompare(b.rawName);
      }

      switch (sortByvalue) {
        case SortByType.Name:
          return a.rawName.localeCompare(b.rawName);
        case SortByType.Completed:
          return nodeDataStatus(b.id) - nodeDataStatus(a.id);
        default:
          return 0;
      }
    }) : dataListItems;

    const designHeading = structuredData ? t(translations.structuredData) : t(translations.unstructuredData);
    const heading = activeItemIsDesign ? designHeading : t(translations.files);
    const objectCount = separatedObjectList.length;

    if (!structuredData) {
      return (
        <>
          <FilesBrowser
            key={activeItem?.id}
            heading={`${heading} (${objectCount})`}
            items={sortedList}
            isLoading={isLoading}
            rootParentNodeId={hierarchyActiveItem?.nodeId}
            onAddCatalogue={addNode}
            onUploadFile={openUploadModal}
            onDropFiles={onDrop}
            onMoveItem={handleMoveItem}
          />
          {renderMoveFileDialog()}
        </>
      );
    }

    const emptyUnfilteredList = unfilteredDataListItems.filter((item) => item.parent === 0).length === 0;
    const emptyFilteredList = dataListItems.filter((item) => item.parent === 0).length === 0;
    const visibleItems = !emptyFilteredList;

    return (
      <div>
        <Flex justifyContent="space-between" alignItems="center" wrap={false}>
          <Heading>
            {activeItem && heading.concat(` (${objectCount}) `)}
            <Spacer width="var(--padding-xxs)" />
            {isLoading && <Flex justifyContent="center"><Spinner dark tiny /></Flex>}
          </Heading>
          <Flex alignItems="center">
            <Input
              value={filterText}
              placeholder="Search"
              width={200}
              onChange={
                (event: React.ChangeEvent<{ value: string }>) => setFilterText(event.target.value)
              }
            />
            <Spacer width="var(--padding)" />
            <Select width={150} value={sortByvalue} options={sortByOptions} onChange={(event: any) => setSortByValue(event.target.value)} />
          </Flex>
        </Flex>
        <Spacer />
        {visibleItems && (
          <Card padding={false}>
            <Tree
              list={{
                items: sortedList,
              }}
              itemHasChild={itemHasChild}
              isInitialOpen={openTreeNodes}
              autoOpenActiveItems
              icons={{
                expand: <TbFolder />,
                collapse: <TbFolderOpen />,
              }}
            />
          </Card>
        )}
        {emptyUnfilteredList && !isLoading && empty(structuredData)}
        {!emptyUnfilteredList && emptyFilteredList && !isLoading && emptySearch}
        <Spacer />
      </div>
    );
  };

  return (
    <div>
      {activeItem
        && (
          <>
            {activeItem.entityType !== HierarchyLevelType.Design
              && hierarchyItemChildList}
            <Spacer />
          </>
        )}
      {activeItemIsDesign && renderDataList(true)}
      {activeItem?.entityType !== HierarchyLevelType.Archive && renderDataList()}
      {activeItem && activeItem?.entityType !== HierarchyLevelType.Archive && (
        <DataExplorerFileDrop onDrop={onDrop} itemName={hierarchyActiveItem?.name || ''} />
      )}
      {nodeId && isOpenUploadModal
        && (
          <FileUpload
            isModal
            visible={isOpenUploadModal}
            onClose={closeUploadModal}
            initialNodeId={nodeId || activeItem?.id}
            onUploadComplete={() => onUploadComplete(nodeId)}
            archiveid={selectedArchiveId!}
            initialFiles={files}
          />
        )}
      {isOpenUnpackModal && (
        <UnpackModal
          name={selectedData?.metadata?.file?.name}
          visible={isOpenUnpackModal}
          onClose={closeUnpackModal}
          onPreUnpackObject={(archiveid: string, country: string) => preUnpackObject({ objectid: selectedData!.objectid, archiveid, country })}
          onUnpackObject={(selectedCountryName: string, selectedDesingIds: string[], archiveid: string) => {
            unpackObject({
              archiveid, country: selectedCountryName, selectedDesingIds, objectid: selectedData!.objectid,
            });
          }}
        />
      )}
      {isOpenShareModal && (
        <ShareObjectModal
          objectid={selectedData!.objectid}
          name={selectedData!.name}
          visible={isOpenShareModal}
          onClose={closeShareModal}
        />
      )}
      <AccessControlModal
        ref={childRef}
        handleEntityAccessRightsUpdated={setAccessRightsBatch}
      />
      {
        isOpenClassificationLevelControlModal ? (
          <ClassificationLevelControlModal
            onCloseClassificationLevelControlModal={onCloseClassificationLevelControlModal}
            showModal={isOpenClassificationLevelControlModal}
            selectedItem={classificationLevelHierarchyItem}
          />
        ) : null
      }
    </div>
  );
};

const mapStateToProps = ({ entities, ui }: RootState) => ({
  objects: entities?.object.objects,
  objectsLoading: entities?.object.isLoading,
  nodesPages: entities?.nodeTree.pages,
  nodes: entities?.nodeTree.items,
  nodesLoading: entities?.nodeTree.isLoading,
  isLoadingIPMFiles: entities?.ipmFiles.isLoading,
  activeItem: ui.hierarchyTree.activeItem,
  users: entities?.user.users,
  entityAccessRightsList: entities?.entityAccessRights.accessRights,
  entityAccessRightsMetadata: entities?.entityAccessRights.metadata,
  selectedCustomer: entities?.customer.selectedCustomer,
  openedItems: ui.hierarchyTree.openedItems,
  selectedArchiveId: ui.hierarchyTree.selectedArchiveId,
});

const mapDispatchToProps = {
  getNodes,
  getDesignNodes,
  getObjectsOfNode,
  createNode,
  updateNode,
  preUnpackObject,
  unpackObject,
  setActiveItem,
  getUsers,
  addStructuredData,
  moveObjectToNode,
};
const connector = connect(mapStateToProps, mapDispatchToProps);
const Container = connector(DataExplorer);

type PropsFromRedux = ConnectedProps<typeof connector>;

export { Container as DataExplorer };
